import { Box, CircularProgress, Alert } from '@mui/material';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseButton } from 'components/BaseButton';
import { collaboraAllow, withCollaboraDarkTheme } from 'services/auth';
import type { CollaboraSession } from 'services/auth';

type CollaboraFrameProps = {
  session: CollaboraSession;
  darkTheme?: boolean;
};

// Renders the Collabora iframe plus the loading and re-auth states of a useCollaboraSession.
// On a refresh failure the iframe stays mounted (unsaved edits survive); the alert offers a retry.
export const CollaboraFrame: FC<CollaboraFrameProps> = ({ session, darkTheme = false }) => {
  const { t } = useTranslation();
  const {
    status, src, frameKey, iframeRef, retry,
  } = session;
  const frameSrc = src !== null && darkTheme ? withCollaboraDarkTheme(src) : src;

  return (
    <Box display="flex" flexDirection="column" height="100%" minHeight={0}>
      {status === 'reauth' && (
        <Alert
          severity="warning"
          action={(
            <BaseButton
              text={t('common:retry')}
              icon="sync"
              buttonVariant="outlined"
              buttonColor="inherit"
              size="small"
              onClick={retry}
            />
          )}
        >
          {t('auth:wopi-reauth')}
        </Alert>
      )}
      {status === 'loading' && (
        <Box display="flex" flex="1 1 0" alignItems="center" justifyContent="center" role="status" aria-busy="true" aria-label={t('auth:wopi-loading')}>
          <CircularProgress />
        </Box>
      )}
      {frameSrc !== null && (
        <iframe
          key={frameKey}
          ref={iframeRef}
          title="prev"
          src={frameSrc}
          style={{
            display: 'block', width: '100%', flex: '1 1 0', minHeight: 0, border: 0,
          }}
          allow={collaboraAllow()}
        />
      )}
    </Box>
  );
};
